/////////////////////////////////////////////////////////
// Configurations related to listing.                  //
// Main configuration here is the extended data config //
/////////////////////////////////////////////////////////

// Note: The listingFields come from listingFields asset nowadays by default.
//       To use this built-in configuration, you need to change the overwrite from configHelper.js
//       (E.g. use mergeDefaultTypesAndFieldsForDebugging func)

/**
 * Configuration options for listing fields (custom extended data fields):
 * - key:                           Unique key for the extended data field.
 * - scope (optional):              Scope of the extended data can be either 'public' or 'private'.
 *                                  Default value: 'public'.
 *                                  Note: listing doesn't support 'protected' scope atm.
 * - includeForListingTypes:        An array of listing types, for which the extended
 *   (optional)                     data is relevant and should be added.
 * - schemaType (optional):         Schema for this extended data field.
 *                                  This is relevant when rendering components and querying listings.
 *                                  Possible values: 'enum', 'multi-enum', 'text', 'long', 'boolean'.
 * - enumOptions (optional):        Options shown for 'enum' and 'multi-enum' extended data.
 *                                  These are used to render options for inputs and filters on
 *                                  EditListingPage, ListingPage, and SearchPage.
 * - filterConfig:                  Filter configuration for listings query.
 *    - indexForSearch (optional):    If set as true, it is assumed that the extended data key has
 *                                    search index in place. I.e. the key can be used to filter
 *                                    listing queries (then scope needs to be 'public').
 *                                    Note: Flex CLI can be used to set search index for the key:
 *                                    https://www.sharetribe.com/docs/references/extended-data/#search-schema
 *                                    Read more about filtering listings with public data keys from API Reference:
 *                                    https://www.sharetribe.com/api-reference/marketplace.html#extended-data-filtering
 *                                    Default value: false,
 *   - filterType:                    Sometimes a single schemaType can be rendered with different filter components.
 *                                    For 'enum' schema, filterType can be 'SelectSingleFilter' or 'SelectMultipleFilter'
 *   - label:                         Label for the filter, if the field can be used as query filter
 *   - searchMode (optional):         Search mode for indexed data with multi-enum schema.
 *                                    Possible values: 'has_all' or 'has_any'.
 *   - group:                         SearchPageWithMap has grouped filters. Possible values: 'primary' or 'secondary'.
 * - showConfig:                    Configuration for rendering listing. (How the field should be shown.)
 *   - label:                         Label for the saved data.
 *   - isDetail                       Can be used to hide detail row (of type enum, boolean, or long) from listing page.
 *                                    Default value: true,
 * - saveConfig:                    Configuration for adding and modifying extended data fields.
 *   - label:                         Label for the input field.
 *   - placeholderMessage (optional): Default message for user input.
 *   - isRequired (optional):         Is the field required for providers to fill
 *   - requiredMessage (optional):    Message for those fields, which are mandatory.
 */
export const listingFields = [
  {
    key: 'locationType',
    scope: 'public',
    includeForListingTypes: ['store-listing'],
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'business-district', label: 'Business district', labelDe: 'Geschäftsviertel' },
      { option: 'creative-art-district', label: 'Creative/art district', labelDe: 'Kunst- und Kreativviertel' },
      { option: 'event-driven-location', label: 'Event-driven location', labelDe: 'Kultur- und Eventviertel' },
      { option: 'mall-shopping-center', label: 'Mall/shopping center', labelDe: 'Einkaufszentrum' },
      { option: 'market-hall', label: 'Market hall', labelDe: 'Markthalle' },
      { option: 'mixed-use-area', label: 'Mixed-use area', labelDe: 'Gemischt genutztes Gebiet' },
      { option: 'cultural', label: 'Near cultural attraction', labelDe: 'Nähe kultureller Sehenswürdigkeiten' },
      { option: 'public-transit', label: 'Near public transit hub', labelDe: 'Nähe eines Verkehrsknotenpunkts' },
      { option: 'residential-neighborhood', label: 'Residential neighborhood', labelDe: 'Wohngebiet' },
      { option: 'shopping-street', label: 'Shopping street', labelDe: 'Einkaufsstraße' },
      { option: 'tourist-area', label: 'Tourist area', labelDe: 'Touristenviertel' },
      { option: 'university-area', label: 'University area', labelDe: 'Universitätsviertel' },
    ],
    associations: {
      'business-district': { label: 'Business district', labelDe: 'Geschäftsviertel' },
      'creative-art-district': { label: 'Creative/art district', labelDe: 'Kunst- und Kreativviertel' },
      'event-driven-location': { label: 'Event-driven location', labelDe: 'Kultur- und Eventviertel' },
      'mall-shopping-center': { label: 'Mall/shopping center', labelDe: 'Einkaufszentrum' },
      'market-hall': { label: 'Market hall', labelDe: 'Markthalle' },
      'mixed-use-area': { label: 'Mixed-use area', labelDe: 'Gemischt genutztes Gebiet' },
      cultural: { label: 'Near cultural attraction', labelDe: 'Nähe kultureller Sehenswürdigkeiten' },
      'public-transit': { label: 'Near public transit hub', labelDe: 'Nähe eines Verkehrsknotenpunkts' },
      'residential-neighborhood': { label: 'Residential neighborhood', labelDe: 'Wohngebiet' },
      'shopping-street': { label: 'Shopping street', labelDe: 'Einkaufsstraße' },
      'tourist-area': { label: 'Tourist area', labelDe: 'Touristenviertel' },
      'university-area': { label: 'University area', labelDe: 'Universitätsviertel' },
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Location',
      labelDe: 'Lage',
      group: 'primary',
    },
    showConfig: {
      label: 'Location type',
      labelDe: 'Lagetyp',
      isDetail: true,
    },
    saveConfig: {
      label: 'Location type',
      placeholderMessage: 'Select an option…',
      requiredMessage: 'You need to select a location type.',
      labelDe: 'Lagetyp',
      placeholderMessageDe: 'Bitte mindestens eine Option auswählen…',
      requiredMessageDe: 'Du musst einen Lagetyp auswählen.',
      isRequired: true,
    },
  },
  {
    key: 'rentalSpaceDisplayTypes',
    scope: 'public',
    includeForListingTypes: ['store-listing'],
    // schemaType: 'display-type',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'clothingRack', label: 'Clothing racks', labelDe: 'Kleiderständer', icon: true },
      { option: 'windowDisplay', label: 'Window displays', labelDe: 'Schaufenster-Displays', icon: true },
      { option: 'tableDisplay', label: 'Table displays', labelDe: 'Tisch-Displays', icon: true },
      { option: 'glassDisplay', label: 'Glass displays', labelDe: 'Glasvitrinen', icon: true },
      { option: 'mannequin', label: 'Mannequins', labelDe: 'Schaufensterpuppen', icon: true },
      { option: 'shelfDisplay', label: 'Shelf displays', labelDe: 'Regal-Displays', icon: true },
      { option: 'countertops', label: 'Countertops', labelDe: 'Verkaufstheken', icon: true },
      { option: 'otherDisplay', label: 'Other displays', labelDe: 'Andere Displays', icon: true },
    ],
    associations: {
      clothingRack: { label: 'Clothing racks', labelDe: 'Kleiderständer' },
      windowDisplay: { label: 'Window displays', labelDe: 'Schaufenster-Displays' },
      tableDisplay: { label: 'Table displays', labelDe: 'Tisch-Displays' },
      glassDisplay: { label: 'Glass displays', labelDe: 'Glasvitrinen' },
      mannequin: { label: 'Mannequins', labelDe: 'Schaufensterpuppen' },
      shelfDisplay: { label: 'Shelf displays', labelDe: 'Regal-Displays' },
      countertops: { label: 'Countertops', labelDe: 'Verkaufstheken' },
      otherDisplay: { label: 'Other displays', labelDe: 'Andere Displays' },
    },
    filterConfig: {
      // indexForSearch: true,
      // filterType: 'SelectSingleFilter',
      // label: 'Display type',
      // searchMode: 'has_any',
      // group: 'primary',

      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Display',
      labelDe: 'Display',
      group: 'primary',
    },
    showConfig: {
      label: 'Display type',
      labelDe: 'Display-Typ',
      isDetail: true,
    },
    saveConfig: {
      label: 'Display type',
      placeholderMessage: 'Select an option…',
      requiredMessage: 'You need to select a display type.',
      labelDe: 'Displaytyp',
      placeholderMessageDe: 'Bitte mindestens eine Option auswählen…',
      requiredMessageDe: 'Du musst einen Display-Typ auswählen.',
      isRequired: true,
    },
  },

  {
    key: 'productFit',
    scope: 'public',
    includeForListingTypes: ['store-listing'],
    // schemaType: 'display-type',
    schemaType: 'group',
    enumOptionsOrder: [
      { filter: 'productGroup', label: 'Product group', labelDe: 'Produktgruppe' },
      { filter: 'productType', label: 'Category', labelDe: 'Kategorie' },
      { filter: 'productPositioning', label: 'Product positioning', labelDe: 'Preispositionierung' },
      { filter: 'storeLabels', label: 'Style & Function', labelDe: 'Stilrichtung & Funktion' },
      { filter: 'storeLabels_2', label: '' },
    ],
    enumOptions: {
      productGroup: [
        { option: 'women', label: 'Women', labelDe: 'Damen' },
        { option: 'men', label: 'Men', labelDe: 'Herren' },
        { option: 'kids', label: 'Kids', labelDe: 'Kinder' },
        { option: 'pets', label: 'Pets', labelDe: 'Haustiere' },
        { option: 'home', label: 'Home', labelDe: 'Wohnen' },
      ],
      productType: [
        { option: 'clothing', label: 'Clothing', labelDe: 'Bekleidung' },
        { option: 'shoes', label: 'Shoes', labelDe: 'Schuhe' },
        { option: 'bags', label: 'Bags', labelDe: 'Taschen' },
        { option: 'accessories', label: 'Accessories', labelDe: 'Accessoires' },
        { option: 'jewellery-and-watches', label: 'Jewellery & Watches', labelDe: 'Schmuck & Uhren' },
        { option: 'home-textiles', label: 'Home Textiles', labelDe: 'Heimtextilien' },
        { option: 'other-items', label: 'Other items', labelDe: 'Andere Artikel' },
      ],
      productPositioning: [
        { option: '0-50', label: 'Budget under €50', labelDe: 'Budget unter 50 €' },
        { option: '50-150', label: 'Mid-range €50–150', labelDe: 'Mittleres Segment 50–150 €' },
        { option: '150-400', label: 'Premium €150–400', labelDe: 'Premium 150–400 €' },
        { option: '400+', label: 'Luxury €400+', labelDe: 'Luxus ab 400 €' },
      ],
      storeLabels: [
        // { option: 'activewear', label: 'Activewear' },
        // { option: 'costumes', label: 'Costumes' },
        // { option: 'artsy', label: 'Edgy/Artsy' },
        // { option: 'sustainable', label: 'Fair & Sustainable' },
        // { option: 'tailoring', label: 'Formal/Tailoring' },
        // { option: 'intimates', label: 'Intimates' },
        // { option: 'maternity', label: 'Maternity' },
        // { option: 'minimalist', label: 'Minimalist' },
        { option: 'trend-oriented', label: 'Trend-oriented', labelDe: 'Trendorientiert' },
        { option: 'timeless-and-basics', label: 'Timeless & Basics', labelDe: 'Zeitlose Basics' },
        { option: 'minimalist', label: 'Minimalist', labelDe: 'Minimalistisch' },
        { option: 'fair-and-sustainable', label: 'Fair & Sustainable', labelDe: 'Fair & Nachhaltig' },
        { option: 'streetwear-casual', label: 'Streetwear/Casual', labelDe: 'Freizeit & Streetwear' },
        { option: 'activewear', label: 'Activewear', labelDe: 'Sportbekleidung' },
        { option: 'outdoor', label: 'Outdoor', labelDe: 'Outdoor' },
        { option: 'formal-tailoring', label: 'Formal/Tailoring', labelDe: 'Business & Anzugmode' },
      ],
      storeLabels_2: [
        { option: 'special-occasion', label: 'Special occasion', labelDe: 'Besondere Anlässe' },
        { option: 'intimates', label: 'Intimates', labelDe: 'Unterwäsche & Lingerie' },
        { option: 'swimwear', label: 'Swimwear', labelDe: 'Bademode' },
        { option: 'plus-size', label: 'Plus-size', labelDe: 'Große Größen' },
        { option: 'maternity', label: 'Maternity', labelDe: 'Umstandsmode' },
        { option: 'edgy-artsy', label: 'Edgy/Artsy', labelDe: 'Kreativ & Ausgefallen' },
        { option: 'costumes', label: 'Costumes', labelDe: 'Kostüme' },
        // { option: 'outdoor', label: 'Outdoor' },
        // { option: 'plus-size', label: 'Plus-size' },
        // { option: 'special-occasion', label: 'Special occasion' },
        // { option: 'streetwear', label: 'Streetwear/Casual' },
        // { option: 'swimwear', label: 'Swimwear' },
        // { option: 'timeless', label: 'Timeless & Basics' },
        // { option: 'trend-oriented', label: 'Trend-oriented' },
      ],
    },
    associations: {
      clothingRack: 'Clothing racks',
      windowDisplay: 'Window displays',
      tableDisplay: 'Table displays',
      glassDisplay: 'Glass displays',
      mannequin: 'Mannequins',
      shelfDisplay: 'Shelf displays',
      countertops: 'Countertops',
      otherDisplay: 'Other displays',
    },
    filterConfig: {
      // indexForSearch: true,
      // filterType: 'SelectSingleFilter',
      // label: 'Display type',
      // searchMode: 'has_any',
      // group: 'primary',

      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Product fit',
      labelDe: 'Produktkriterien',
      group: 'primary',
    },
    showConfig: {
      label: 'Product fit',
      labelDe: 'Produktkriterien',
      isDetail: true,
    },
    saveConfig: {
      label: 'Display type',
      placeholderMessage: 'Select an option…',
      requiredMessage: 'You need to select a display type.',
      labelDe: 'Produktkriterien',
      placeholderMessageDe: 'Bitte mindestens eine Option auswählen…',
      requiredMessageDe: 'Du musst Produktkriterien auswählen.',
      isRequired: true,
    },
  },

  {
    key: 'productGroup',
    scope: 'public',
    includeForListingTypes: ['store-listing'],
    schemaType: 'multi-enum',
    hideOnSearch: true,
    enumOptions: [
      { option: 'women', label: 'Women', labelDe: 'Damen' },
      { option: 'men', label: 'Men', labelDe: 'Herren' },
      { option: 'kids', label: 'Kids', labelDe: 'Kinder' },
      { option: 'pets', label: 'Pets', labelDe: 'Haustiere' },
      { option: 'home', label: 'Home', labelDe: 'Wohnen' },
    ],
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Product group',
      labelDe: 'Produktgruppe',
      group: 'primary',
    },
    showConfig: {
      label: 'Product group',
      labelDe: 'Produktgruppe',
      isDetail: true,
    },
    saveConfig: {
      label: 'Product group',
      placeholderMessage: 'Select an option…',
      requiredMessage: 'You need to select a product group.',
      labelDe: 'Produktgruppe',
      placeholderMessageDe: 'Bitte mindestens eine Option auswählen…',
      requiredMessageDe: 'Du musst eine Produktgruppe auswählen.',
      isRequired: true,
    },
  },
  {
    key: 'productType',
    scope: 'public',
    includeForListingTypes: ['store-listing'],
    schemaType: 'multi-enum',
    hideOnSearch: true,
    enumOptions: [
      { option: 'clothing', label: 'Clothing', labelDe: 'Bekleidung' },
      { option: 'shoes', label: 'Shoes', labelDe: 'Schuhe' },
      { option: 'bags', label: 'Bags', labelDe: 'Taschen' },
      { option: 'accessories', label: 'Accessories', labelDe: 'Accessoires' },
      { option: 'jewellery', label: 'Jewellery & Watches', labelDe: 'Schmuck & Uhren' },
      { option: 'home-textiles', label: 'Home Textiles', labelDe: 'Heimtextilien' },
      { option: 'other-items', label: 'Other items', labelDe: 'Andere Artikel' },
    ],
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Product type',
      labelDe: 'Produkttyp',
      group: 'primary',
    },
    showConfig: {
      label: 'Category',
      labelDe: 'Produkttyp',
      isDetail: true,
    },
    saveConfig: {
      label: 'Product type',
      placeholderMessage: 'Select an option…',
      requiredMessage: 'You need to select a product type.',
      labelDe: 'Produkttyp',
      placeholderMessageDe: 'Bitte mindestens eine Option auswählen…',
      requiredMessageDe: 'Du musst einen Produkttyp auswählen.',
      isRequired: true,
    },
  },

  // {
  //   key: 'rentalSpaceDisplayTypes',
  //   scope: 'public',
  //   includeForListingTypes: ['store-listing'],
  //   schemaType: 'multi-enum',
  //   enumOptions: [
  //     { option: 'clothing', label: 'Clothing' },
  //     { option: 'shoes', label: 'Shoes' },
  //     { option: 'bags', label: 'Bags' },
  //     { option: 'accessories', label: 'Accessories' },
  //     { option: 'jewellery', label: 'Jewellery' },
  //     { option: 'textiles', label: 'Textiles' },
  //     { option: 'other-items', label: 'Other items' },
  //   ],
  //   filterConfig: {
  //     indexForSearch: true,
  //     filterType: 'SelectMultipleFilter',
  //     label: 'Display',
  //     group: 'primary',
  //   },
  //   showConfig: {
  //     label: 'Display',
  //     isDetail: true,
  //   },
  //   // saveConfig: {
  //   //   label: 'Display',
  //   //   placeholderMessage: 'Select an option…',
  //   //   isRequired: true,
  //   //   requiredMessage: 'You need to select a product type.',
  //   // },
  // },

  {
    key: 'productPositioning',
    scope: 'public',
    includeForListingTypes: ['store-listing'],
    schemaType: 'multi-enum',
    hideOnSearch: true,
    enumOptions: [
      { option: '0-50', label: 'Budget under €50', labelDe: 'Budget unter 50 €' },
      { option: '50-150', label: 'Mid-range €50–150', labelDe: 'Mittleres Segment 50–150 €' },
      { option: '150-400', label: 'Premium €150–400', labelDe: 'Premium 150–400 €' },
      { option: '400+', label: 'Luxury €400+', labelDe: 'Luxus ab 400 €' },
    ],
    associations: {
      '0-50': { label: 'Budget under €50', labelDe: 'Budget unter 50 €' },
      '50-150': { label: 'Mid-range €50–150', labelDe: 'Mittleres Segment 50–150 €' },
      '150-400': { label: 'Premium €150–400', labelDe: 'Premium 150–400 €' },
      '400+': { label: 'Luxury €400+', labelDe: 'Luxus ab 400 €' },
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Product positioning',
      labelDe: 'Preispositionierung',
      group: 'primary',
    },
    showConfig: {
      label: 'Product positioning',
      labelDe: 'Preispositionierung',
      isDetail: true,
    },
    saveConfig: {
      label: 'Product positioning',
      placeholderMessage: 'Select an option…',
      requiredMessage: 'You need to select a product positioning.',
      labelDe: 'Preispositionierung',
      placeholderMessageDe: 'Bitte mindestens eine Option auswählen…',
      requiredMessageDe: 'Du musst eine Preispositionierung auswählen.',
      isRequired: true,
    },
  },
  {
    key: 'storeLabels',
    scope: 'public',
    includeForListingTypes: ['store-listing'],
    schemaType: 'multi-enum',
    hideOnSearch: true,
    enumOptions: [
      { option: 'trend-oriented', label: 'Trend-oriented', labelDe: 'Trendorientiert' },
      { option: 'timeless-and-basics', label: 'Timeless & Basics', labelDe: 'Zeitlose Basics' },
      { option: 'minimalist', label: 'Minimalist', labelDe: 'Minimalistisch' },
      { option: 'fair-and-sustainable', label: 'Fair & Sustainable', labelDe: 'Fair & Nachhaltig' },
      { option: 'streetwear-casual', label: 'Streetwear/Casual', labelDe: 'Freizeit & Streetwear' },
      { option: 'activewear', label: 'Activewear', labelDe: 'Sportbekleidung' },
      { option: 'outdoor', label: 'Outdoor', labelDe: 'Outdoor' },
      { option: 'formal-tailoring', label: 'Formal/Tailoring', labelDe: 'Business & Anzugmode' },
      { option: 'special-occasion', label: 'Special occasion', labelDe: 'Besondere Anlässe' },
      { option: 'intimates', label: 'Intimates', labelDe: 'Unterwäsche & Lingerie' },
      { option: 'swimwear', label: 'Swimwear', labelDe: 'Bademode' },
      { option: 'plus-size', label: 'Plus-size', labelDe: 'Große Größen' },
      { option: 'maternity', label: 'Maternity', labelDe: 'Umstandsmode' },
      { option: 'edgy-artsy', label: 'Edgy/Artsy', labelDe: 'Kreativ & Ausgefallen' },
      { option: 'costumes', label: 'Costumes', labelDe: 'Kostüme' },
      // { option: "bohemian", label: "Bohemian" },
      // { option: "bridal", label: "Bridal" },
      // { option: "casual", label: "Casual" },
      // { option: "classic", label: "Classic" },
      // { option: "elegant", label: "Elegant" },
      // { option: "ethnic", label: "Ethnic" },
      // { option: "evening", label: "Evening" },
      // { option: "fair-trade", label: "Fair-trade" },
      // { option: "formal", label: "Formal" },
      // { option: "gothic", label: "Gothic" },
      // { option: "green", label: "Green" },
      // { option: "lingerie", label: "Lingerie" },
      // { option: "minimalist", label: "Minimalist" },
      // { option: "modern", label: "Modern" },
      // { option: "nightwear", label: "Nightwear" },
      // { option: "outdoor", label: "Outdoor" },
      // { option: "punk", label: "Punk" },
      // { option: "retro", label: "Retro" },
      // { option: "sporty", label: "Sporty" },
      // { option: "streetwear", label: "Streetwear" },
      // { option: "suits", label: "Suits" },
      // { option: "swimwear", label: "Swimwear" },
      // { option: "tailoring", label: "Tailoring" },
      // { option: "timeless", label: "Timeless" },
      // { option: "trend-oriented", label: "Trend oriented" },
      // { option: "underwear", label: "Underwear" },
      // { option: "vintage", label: "Vintage" }
    ],
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand preferences',
      labelDe: 'Markenpräferenzen',
      group: 'primary',
    },
    showConfig: {
      label: 'Brand preferences',
      labelDe: 'Markenpräferenzen',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand preferences',
      placeholderMessage: 'Select an option…',
      requiredMessage: 'You need to select a shop label.',
      labelDe: 'Markenpräferenzen',
      placeholderMessageDe: 'Bitte mindestens eine Option auswählen…',
      requiredMessageDe: 'Du musst ein Shop-Label auswählen.',
      isRequired: true,
    },
  },
  {
    key: 'sustainabilityFactors',
    scope: 'public',
    includeForListingTypes: ['brand-listing'],
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'social-commitment', label: 'Social commitment', labelDe: 'Soziales Engagement' },
      { option: 'artisanal', label: 'Artisanal', labelDe: 'Handwerklich gefertigt' },
      { option: 'carbon-reduction', label: 'Carbon reduction', labelDe: 'CO2-Einsparung' },
      { option: 'charitable-donation', label: 'Charitable donation', labelDe: 'Spendenbeteiligung' },
      { option: 'animal-welfare-materials', label: 'Animal welfare materials', labelDe: 'Tierwohl-Materialien' },
      { option: 'cruelty-free', label: 'Cruelty-free', labelDe: 'Tierversuchsfrei' },
      { option: 'environmentally-friendly', label: 'Environmentally friendly', labelDe: 'Umweltfreundlich' },
      { option: 'ethical-leadership', label: 'Ethical leadership', labelDe: 'Ethischer Führungsstil' },
      { option: 'ethically-made', label: 'Ethically made', labelDe: 'Ethisch hergestellt' },
      { option: 'personalised', label: 'Personalised', labelDe: 'Personalisiert' },
      { option: 'fair-working-conditions', label: 'Fair working conditions', labelDe: 'Faire Arbeitsbedingungen' },
      { option: 'independent-brand', label: 'Independent brand', labelDe: 'Unabhängige Marke' },
      { option: 'locally-made', label: 'Locally made', labelDe: 'Regional gefertigt' },
      { option: 'natural-materials', label: 'Natural materials', labelDe: 'Natürliche Materialien' },
      { option: 'non-toxic-dyes', label: 'Non-toxic dyes', labelDe: 'Schadstofffreie Farben' },
      { option: 'one-of-a-kind', label: 'One of a kind', labelDe: 'Einzelstück' },
      { option: 'organic', label: 'Organic', labelDe: 'Bio-zertifiziert' },
      { option: 're-upcycled-materials', label: 'Re/upcycled materials', labelDe: 'Re-/Upcycling' },
      { option: 'responsible-packaging', label: 'Responsible packaging', labelDe: 'Nachhaltige Verpackung' },
      { option: 'vegan-materials', label: 'Vegan materials', labelDe: 'Vegane Materialien' },
    ],
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Sustainability Factors',
      labelDe: 'Nachhaltigkeit & Ethik',
      group: 'primary',
    },
    showConfig: {
      label: 'Sustainability Factors',
      labelDe: 'Nachhaltigkeit & Ethik',
      isDetail: true,
    },
    saveConfig: {
      label: 'Sustainability Factors',
      placeholderMessage: 'Select an option…',
      requiredMessage: 'You need to select a sustainability factor.',
      labelDe: 'Nachhaltigkeit & Ethik',
      placeholderMessageDe: 'Bitte mindestens eine Option auswählen…',
      requiredMessageDe: 'Du musst einen Nachhaltigkeitsfaktor auswählen.',
      isRequired: true,
    },
  },

  // {
  //   key: 'category',
  //   scope: 'public',
  //   schemaType: 'enum',
  //   enumOptions: [
  //     { option: 'city-bikes', label: 'City bikes' },
  //     { option: 'electric-bikes', label: 'Electric bikes' },
  //     { option: 'mountain-bikes', label: 'Mountain bikes' },
  //     { option: 'childrens-bikes', label: "Children's bikes" },
  //   ],
  //   filterConfig: {
  //     indexForSearch: true,
  //     filterType: 'SelectMultipleFilter',
  //     label: 'Category',
  //     group: 'primary',
  //   },
  //   showConfig: {
  //     label: 'Category',
  //     isDetail: true,
  //   },
  //   saveConfig: {
  //     label: 'Category',
  //     placeholderMessage: 'Select an option…',
  //     isRequired: true,
  //     requiredMessage: 'You need to select a category.',
  //   },
  // },
  // {
  //   key: 'tire',
  //   scope: 'public',
  //   schemaType: 'enum',
  //   enumOptions: [
  //     { option: '29', label: '29' },
  //     { option: '28', label: '28' },
  //     { option: '27', label: '27' },
  //     { option: '26', label: '26' },
  //     { option: '24', label: '24' },
  //     { option: '20', label: '20' },
  //     { option: '18', label: '18' },
  //   ],
  //   filterConfig: {
  //     indexForSearch: true,
  //     label: 'Tire size',
  //     group: 'secondary',
  //   },
  //   showConfig: {
  //     label: 'Tire size',
  //     isDetail: true,
  //   },
  //   saveConfig: {
  //     label: 'Tire size',
  //     placeholderMessage: 'Select an option…',
  //     isRequired: true,
  //     requiredMessage: 'You need to select a tire size.',
  //   },
  // },
  // {
  //   key: 'brand',
  //   scope: 'public',
  //   schemaType: 'enum',
  //   enumOptions: [
  //     { option: 'cube', label: 'Cube' },
  //     { option: 'diamant', label: 'Diamant' },
  //     { option: 'ghost', label: 'GHOST' },
  //     { option: 'giant', label: 'Giant' },
  //     { option: 'kalkhoff', label: 'Kalkhoff' },
  //     { option: 'kona', label: 'Kona' },
  //     { option: 'otler', label: 'Otler' },
  //     { option: 'vermont', label: 'Vermont' },
  //   ],
  //   filterConfig: {
  //     indexForSearch: true,
  //     label: 'Brand',
  //     group: 'secondary',
  //   },
  //   showConfig: {
  //     label: 'Brand',
  //     isDetail: true,
  //   },
  //   saveConfig: {
  //     label: 'Brand',
  //     placeholderMessage: 'Select an option…',
  //     isRequired: true,
  //     requiredMessage: 'You need to select a brand.',
  //   },
  // },
  // {
  //   key: 'accessories',
  //   scope: 'public',
  //   schemaType: 'multi-enum',
  //   enumOptions: [
  //     { option: 'bell', label: 'Bell' },
  //     { option: 'lights', label: 'Lights' },
  //     { option: 'lock', label: 'Lock' },
  //     { option: 'mudguard', label: 'Mudguard' },
  //   ],
  //   filterConfig: {
  //     indexForSearch: true,
  //     label: 'Accessories',
  //     searchMode: 'has_all',
  //     group: 'secondary',
  //   },
  //   showConfig: {
  //     label: 'Accessories',
  //   },
  //   saveConfig: {
  //     label: 'Accessories',
  //     placeholderMessage: 'Select an option…',
  //     isRequired: false,
  //   },
  // },

  // // An example of how to use transaction type specific custom fields and private data.
  // {
  //   key: 'note',
  //   scope: 'public',
  //   includeForListingTypes: ['product-selling'],
  //   schemaType: 'text',
  //   showConfig: {
  //     label: 'Extra notes',
  //   },
  //   saveConfig: {
  //     label: 'Extra notes',
  //     placeholderMessage: 'Some public extra note about this bike...',
  //   },
  // },
  // {
  //   key: 'privatenote',
  //   scope: 'private',
  //   includeForListingTypes: ['daily-booking'],
  //   schemaType: 'text',
  //   saveConfig: {
  //     label: 'Private notes',
  //     placeholderMessage: 'Some private note about this bike...',
  //   },
  // },
];

///////////////////////////////////////////////////////////////////////
// Configurations related to listing types and transaction processes //
///////////////////////////////////////////////////////////////////////

// A presets of supported listing configurations
//
// Note 1: The listingTypes come from listingTypes asset nowadays by default.
//         To use this built-in configuration, you need to change the overwrite from configHelper.js
//         (E.g. use mergeDefaultTypesAndFieldsForDebugging func)
// Note 2: transaction type is part of listing type. It defines what transaction process and units
//         are used when transaction is created against a specific listing.

/**
 * Configuration options for listing experience:
 * - listingType:         Unique string. This will be saved to listing's public data on
 *                        EditListingWizard.
 * - label                Label for the listing type. Used as microcopy for options to select
 *                        listing type in EditListingWizard.
 * - transactionType      Set of configurations how this listing type will behave when transaction is
 *                        created.
 *   - process              Transaction process.
 *                          The process must match one of the processes that this client app can handle
 *                          (check src/util/transactions/transaction.js) and the process must also exists in correct
 *                          marketplace environment.
 *   - alias                Valid alias for the aforementioned process. This will be saved to listing's
 *                          public data as transctionProcessAlias and transaction is initiated with this.
 *   - unitType             Unit type is mainly used as pricing unit. This will be saved to
 *                          transaction's protected data.
 *                          Recommendation: don't use same unit types in completely different processes
 *                          ('item' sold should not be priced the same as 'item' booked).
 * - stockType            This is relevant only to listings using default-purchase process.
 *                        If set to 'oneItem', stock management is not showed and the listing is
 *                        considered unique (stock = 1).
 *                        Possible values: 'oneItem' and 'multipleItems'.
 *                        Default: 'multipleItems'.
 * - defaultListingFields These are tied to transaction processes. Different processes have different flags.
 *                        E.g. default-inquiry can toggle price and location to true/false value to indicate,
 *                        whether price (or location) tab should be shown. If defaultListingFields.price is not
 *                        explicitly set to _false_, price will be shown.
 *                        If the location or pickup is not used, listing won't be returned with location search.
 *                        Use keyword search as main search type if location is not enforced.
 *                        The payoutDetails flag allows provider to bypass setting of payout details.
 *                        Note: customers can't order listings, if provider has not set payout details! Monitor
 *                        providers who have not set payout details and contact them to ensure that they add the details.
 */

export const listingTypes = [
  {
    listingType: 'store-listing',
    label: 'Shop listing',
    labelDe: 'Shopprofil',
    transactionType: {
      process: 'default-store',
      alias: 'default-store/release-1',
      unitType: 'item',
    },
    defaultListingFields: {
      location: false,
      payoutDetails: false,
    },
  },
  {
    listingType: 'brand-listing',
    label: 'Brand listing',
    labelDe: 'Markenprofil',
    transactionType: {
      process: 'default-brand',
      alias: 'default-brand/release-1',
      unitType: 'item',
    },
    defaultListingFields: {
      location: false,
      payoutDetails: false,
    },
  },
  {
    listingType: 'daily-booking',
    label: 'Shop renting space listing',
    labelDe: 'Display-Buchung',
    transactionType: {
      process: 'default-booking',
      alias: 'default-booking/release-1',
      unitType: 'day',
    },
    defaultListingFields: {
      location: false,
      payoutDetails: false,
    },
  },
  {
    listingType: 'product-selling',
    label: 'Sell clothes',
    labelDe: 'Produkte verkaufen',
    transactionType: {
      process: 'default-purchase',
      alias: 'default-purchase/release-1',
      unitType: 'item',
    },
    defaultListingFields: {
      location: false,
      payoutDetails: false,
    },
    stockType: 'oneItem',
  },
  // // Here are some examples for other listingTypes
  // // TODO: SearchPage does not work well if both booking and product selling are used at the same time
  // {
  //   listingType: 'nightly-booking',
  //   label: 'Nightly booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'night',
  //   },
  // },
  // {
  //   listingType: 'hourly-booking',
  //   label: 'Hourly booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'hour',
  //   },
  // },
  // {
  //   listingType: 'product-selling',
  //   label: 'Sell bicycles',
  //   transactionType: {
  //     process: 'default-purchase',
  //     alias: 'default-purchase/release-1',
  //     unitType: 'item',
  //   },
  //   stockType: 'multipleItems',
  //   defaultListingFields: {
  //     shipping: true,
  //     pickup: true,
  //     payoutDetails: true,
  //   },
  // },
  // {
  //   listingType: 'inquiry',
  //   label: 'Inquiry',
  //   transactionType: {
  //     process: 'default-inquiry',
  //     alias: 'default-inquiry/release-1',
  //     unitType: 'inquiry',
  //   },
  //   defaultListingFields: {
  //     price: false,
  //     location: true,
  //   },
  // },
];

// SearchPage can enforce listing query to only those listings with valid listingType
// However, it only works if you have set 'enum' type search schema for the public data fields
//   - listingType
//
//  Similar setup could be expanded to 2 other extended data fields:
//   - transactionProcessAlias
//   - unitType
//
// Read More:
// https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
export const enforceValidListingType = false;
